import {
  ascend,
  chain,
  complement,
  equals,
  groupBy,
  groupWith,
  isEmpty,
  mergeWithKey,
  prop,
  propEq,
  sortWith,
  sum,
  values,
} from 'ramda';
import {Validator, booleanString, validateEmail} from '../Util';
import {getName} from './Common';
import Constants from './Constants';
import {isFullName} from './Users';
import {CustomerItem, ItemUnit, ZEUS_ID, ZEUS_NO_SALE_ID} from './CustomerItems';
import {SendEmailEvent} from './Emails';
import {Event} from './Events';
import {ItemDescription} from './ItemDescriptions';
import {
  MerchantOrder,
  MerchantOrderItem,
  ownerPortion,
  saleNetRevenueValue,
} from './MerchantOrders';
import {CancelOwnerPaymentEvent, CashOutEvent} from './OwnerPayments';
import {FlatPickup} from './Pickups';
const {
  PaymentType: {CHECK, PAYPAL, GIFT_CARD},
} = Constants;

export const REMOOV_OWNER_ID = 0;
export const NO_NAME = 'No Name';
export type Owner = {
  id: Maybe<number>;
  phone: Maybe<string>;
  payment_preference: Maybe<string>;
  paypal_email: Maybe<string>;
  payment_name: Maybe<string>;
  gift_card_email: Maybe<string>;
  donation_receipt_name: Maybe<string>;
  receipt_address: Maybe<{
    address?: string;
    city?: string;
    state?: string;
    zipcode?: string;
  }>;
  twilio_blacklisted: Maybe<boolean>;
  registered_at: Maybe<string>;
  location: Maybe<Location>;
  created_at: Maybe<string>;
  updated_at: Maybe<string>;
  is_vip: Maybe<boolean>;
  realtor_offers_opt_in: Maybe<boolean>;
  // User
  user_id: Maybe<number>;
  email: Maybe<string>;
  first_name: Maybe<string>;
  last_name: Maybe<string>;
  blocked: Maybe<boolean>;
  disable_emails: Maybe<boolean>;
  do_not_send_update: Maybe<boolean>;
  last_login: Maybe<string>;
  // Calculated
  balance_due: number;
  amount_paid: number;
};
export type SavedOwner = Owner & {id: number; user_id: number};
export type Location = {
  id: Maybe<number>;
  address: Maybe<string>;
  address_line_2: Maybe<string>;
  city: Maybe<string>;
  state: Maybe<string>;
  zipcode: Maybe<string>;
  phone: Maybe<string>;
};
export type CancelItemUpdateSubscriptionEvent = Event<'cancelItemUpdateSubscription', string>;
export type OptOutOfRealtorOffersByEmailEvent = Event<
  'optOutOfRealtorOffersByEmail',
  {email: string}
>;
export type OptInToRealtorOffersByEmailEvent = Event<
  'optInToRealtorOffersByEmail',
  {email: string}
>;
export type OptInToRealtorOffersEvent = Event<'optInToRealtorOffers', {encrypted_id: string}>;
export type OwnerEvent =
  | MergeOwnersEvent
  | SendEmailEvent
  | CancelOwnerPaymentEvent
  | CashOutEvent
  | CancelItemUpdateSubscriptionEvent
  | OptOutOfRealtorOffersByEmailEvent
  | OptInToRealtorOffersByEmailEvent
  | OptInToRealtorOffersEvent;
export type MergeOwnersEvent = Event<
  'mergeOwners',
  {primary_owner_id: number; secondary_owner_id: number},
  SavedOwner
>;
export const EMPTY_LOCATION: Location = {
  id: null,
  address: null,
  address_line_2: null,
  city: null,
  state: null,
  zipcode: null,
  phone: null,
};
export const EMPTY_OWNER: Owner = {
  id: null,
  amount_paid: 0,
  balance_due: 0,
  email: null,
  first_name: null,
  last_name: null,
  phone: null,
  blocked: null,
  disable_emails: null,
  is_vip: false,
  realtor_offers_opt_in: false,
  do_not_send_update: null,
  last_login: null,
  payment_preference: null,
  paypal_email: null,
  gift_card_email: null,
  payment_name: null,
  donation_receipt_name: null,
  receipt_address: null,
  user_id: null,
  location: EMPTY_LOCATION,
  twilio_blacklisted: null,
  registered_at: null,
  created_at: null,
  updated_at: null,
};
export type OwnerSearch = {
  paginate?: Maybe<{page: number; perPage: number}>;
  look_for?: Maybe<string>;
  sort_by?: Maybe<string>;
  id?: Maybe<OrArray<number>>;
  phone?: Maybe<OrArray<string>>;
  email?: Maybe<OrArray<string>>;
  market?: Maybe<OrArray<string>>;
  realtor_offers_opt_in?: Maybe<boolean>;
};
export function defaultedGetName(
  user?: Maybe<{first_name?: Maybe<string>; last_name?: Maybe<string>}>,
): string {
  return getName(user) || NO_NAME;
}
export function getOwnerFirstName(
  owner: Maybe<Pick<Owner, 'first_name' | 'last_name'>>,
): Maybe<string> {
  if (!owner) return null;
  const name = getName(owner);
  return isNameFake(name) ? null : owner.first_name;
}
export function ownerHasPhoneName(owner: Pick<Owner, 'phone' | 'first_name'>): boolean {
  return owner.first_name === `+1${(owner.phone || '').replace(/\D/g, '')}`;
}
export function isEmailFake(email: Maybe<string>): boolean {
  return !!email && !!email.match(/^support\+1[\d+]+@remoovit.com$/);
}
export function isNameFake(name: Maybe<string>): boolean {
  return !!name && !!name.match(/^No Name( \(C\d+\))*$/);
}
export function ownerHasPhoneEmail(owner: Partial<Owner>): boolean {
  return isEmailFake(owner.email);
}
export function areOwnersMergeable(primaryOwner: Owner, secondaryOwner: Owner): boolean {
  return Object.keys(EMPTY_OWNER).every(key => {
    const a = getValue(primaryOwner, key);
    const b = getValue(secondaryOwner, key);
    return !a || !b || equals(a, b);
  });
  function getValue(owner, key) {
    const value = owner[key];
    if (['id', 'user_id', 'created_at', 'updated_at'].includes(key)) return null;
    if (key === 'first_name' && ownerHasPhoneName(owner)) return null;
    if (key === 'email' && ownerHasPhoneEmail(owner)) return null;
    if (key === 'location') return {...value, id: null};
    return value;
  }
}
export function mergeOwnerData(primaryOwner: Owner, secondaryOwner: Owner): Owner {
  return mergeWithKey(
    (key, a, b) => {
      const aa = getValue(primaryOwner, key, a);
      const bb = getValue(secondaryOwner, key, b);
      return (aa || bb) ?? (key === 'email' ? a : null);
    },
    primaryOwner,
    secondaryOwner,
  );
  function getValue(owner, key, value) {
    if (key === 'first_name' && ownerHasPhoneName(owner)) return null;
    if (key === 'email' && ownerHasPhoneEmail(owner)) return null;
    return value;
  }
}
function validateCheckPaymentName(payment_name?: Maybe<string>): Maybe<string> {
  const requiredError = Validator.required(payment_name);
  if (requiredError) return requiredError;
  return isFullName(payment_name) ? null : 'full name is required (first and last name)';
}
export function validateOwnerCashout(owner: {
  payment_preference?: Maybe<string>;
  paypal_email?: Maybe<string>;
  gift_card_email?: Maybe<string>;
  payment_name?: Maybe<string>;
  location?: Maybe<{
    address?: Maybe<string>;
    address_line_2?: Maybe<string>;
    city?: Maybe<string>;
    state?: Maybe<string>;
    zipcode?: Maybe<string>;
  }>;
  merchantOrderItemIds?: Maybe<number[]>;
}): Record<string, Maybe<string>> {
  const payment_preference = owner.payment_preference;
  const isPaypal = payment_preference === PAYPAL;
  const isCheck = payment_preference === CHECK;
  const isGiftCard = payment_preference === GIFT_CARD;
  return {
    payment_preference: Validator.required(payment_preference),
    paypal_email: isPaypal ? validateEmail(owner.paypal_email) : null,
    gift_card_email: isGiftCard ? validateEmail(owner.gift_card_email) : null,
    payment_name: isCheck ? validateCheckPaymentName(owner.payment_name) : null,
    address: isCheck ? Validator.required(owner?.location?.address) : null,
    city: isCheck ? Validator.required(owner?.location?.city) : null,
    state: isCheck ? Validator.required(owner?.location?.state) : null,
    zipcode: isCheck ? Validator.validateZipcode(owner?.location?.zipcode || '') : null,
    merchantOrderItemIds: isEmpty(owner.merchantOrderItemIds) ? 'select at least one item' : null,
  };
}
export function generateOwnersCSV(owners: Owner[], firstPage: boolean): string {
  const csv = (owner?: Maybe<Owner>) => {
    return {
      ID: owner?.id,
      Name: defaultedGetName(owner),
      Email: owner?.email,
      Phone: owner?.phone,
      'Balance Due': owner?.balance_due,
      'Amount Paid': owner?.amount_paid,
      'Disable Emails': booleanString(owner?.disable_emails),
      'Do Not Send Update': booleanString(owner?.do_not_send_update),
      Blocked: booleanString(owner?.blocked),
      'Created At': owner?.created_at,
    };
  };
  return (
    (firstPage ? [Object.keys(csv())] : [[]])
      // @ts-ignore
      .concat(owners.map(owner => Object.values(csv(owner))))
      .map(row =>
        row
          .map(cell =>
            cell === undefined || cell === null ? '' : `"${String(cell).replace(/"/g, '""')}"`,
          )
          .join(','),
      )
      .join('\n')
  );
}
export function generateOwnerSalesCSV(
  itemUnits: ItemUnit[],
  pickups: FlatPickup[],
  merchantOrderItems: MerchantOrderItem[],
  merchantOrders: MerchantOrder[],
  items: ItemDescription[],
  firstPage: boolean,
): string {
  const csv = (i?: Maybe<ItemUnit & {quantity: number}>) => {
    const merchantOrderItem = merchantOrderItems.find(propEq('id', i?.merchant_order_item_id));
    const merchantOrder =
      merchantOrderItem && merchantOrders.find(propEq('id', merchantOrderItem.merchant_order_id));
    if (i && !merchantOrderItem?.item_sale) return {};
    const pickup = pickups.find(propEq('id', i?.pickup_id));
    const item = items.find(propEq('id', i?.item_description_id));
    return {
      'Remoov SKU': i?.item_description_id,
      'Owner SKU': String(item?.external_sku || ''),
      Category: item?.item_category?.name,
      'Posting Title': item?.posting_title,
      Description: item?.description,
      'Pickup Date': pickup?.pickup_date,
      'Sale Date': merchantOrder?.order_date,
      Quantity: merchantOrderItem?.quantity,
      'Net Unit Price': merchantOrderItem?.item_sale?.unit_price || '',
      'Net Sale Value': merchantOrderItem && saleNetRevenueValue(merchantOrderItem),
      'Owner Portion': merchantOrderItem && ownerPortion(merchantOrderItem),
    };
  };
  const groupedItemUnits = groupWith(
    (a: ItemUnit, b: ItemUnit) =>
      a.owner_id === b.owner_id &&
      a.pickup_id === b.pickup_id &&
      a.truck_item_id === b.truck_item_id &&
      a.item_description_id === b.item_description_id &&
      a.merchant_order_item_id === b.merchant_order_item_id,
    sortWith(
      [
        // @ts-ignore
        ascend(prop('owner_id')),
        // @ts-ignore
        ascend(prop('pickup_id')),
        // @ts-ignore
        ascend(prop('truck_item_id')),
        // @ts-ignore
        ascend(prop('item_description_id')),
        // @ts-ignore
        ascend(prop('merchant_order_item_id')),
      ],
      itemUnits,
    ),
  ).map(row => ({...row[0], quantity: row.length}));
  return (
    (firstPage ? [Object.keys(csv())] : [[]])
      // @ts-ignore
      .concat(groupedItemUnits.map(item => Object.values(csv(item))))
      .filter(complement(isEmpty))
      .map(row =>
        row
          .map(cell =>
            cell === undefined || cell === null ? '' : `"${String(cell).replace(/"/g, '""')}"`,
          )
          .join(','),
      )
      .join('\n')
  );
}
export function generateOwnerItemsCSV(
  items: CustomerItem[],
  merchantOrderItems: MerchantOrderItem[],
  firstPage: boolean,
): string {
  const csv = (items: CustomerItem[]) => {
    return {
      'Owner SKU': items[0]?.external_sku,
      '#collected': sum(items.map(prop('quantity')).filter(Boolean)),
      '#stored': sum(
        items
          .filter(item => ['For Sale', 'Processing'].includes(item?.status))
          .map(prop('quantity'))
          .filter(Boolean),
      ),
      '#donated': sum(
        items
          .filter(item => ['Donated'].includes(item?.status))
          .map(prop('quantity'))
          .filter(Boolean),
      ),
      '#recycled': sum(
        items
          .filter(item => ['Recycled'].includes(item?.status))
          .map(prop('quantity'))
          .filter(Boolean),
      ),
      '#returned': sum(
        items
          .filter(item => ['Returned'].includes(item?.status))
          .map(prop('quantity'))
          .filter(Boolean),
      ),
      '#sold': sum(
        items
          .filter(item => ['Sold', 'Paid'].includes(item?.status))
          .map(prop('quantity'))
          .filter(Boolean),
      ),
      '#auctioned': sum(
        items
          .filter(item => ['Auctioned'].includes(item?.status))
          .map(prop('quantity'))
          .filter(Boolean),
      ),
      total_sales_value: sum(
        chain(
          prop('merchant_order_item_ids'),
          items.filter(item => ['Sold', 'Paid', 'Auctioned'].includes(item?.status)),
        )
          .map(id => merchantOrderItems.find(propEq('id', id)))
          .filter(Boolean)
          .map(moi =>
            moi.quantity && moi.item_sale?.full_unit_price
              ? moi.quantity * moi.item_sale?.full_unit_price
              : 0,
          )
          .filter(Boolean),
      ),
    };
  };
  const groupedItems = values(groupBy(x => x.external_sku || '', items));
  return (
    (firstPage ? [Object.keys(csv([]))] : [[]])
      // @ts-ignore
      .concat(groupedItems.map(items => Object.values(csv(items))))
      .map(row =>
        row
          .map(cell =>
            cell === undefined || cell === null ? '' : `"${String(cell).replace(/"/g, '""')}"`,
          )
          .join(','),
      )
      .join('\n')
  );
}
export function isZeus(ownerId: Maybe<number>) {
  return [ZEUS_ID, ZEUS_NO_SALE_ID].includes(ownerId);
}
export function ownerUid(owner: Maybe<{id: Maybe<number>}>): string {
  return `U${owner?.id}`;
}
